import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import dbUrl from '../../utils/credentials';
import PageHeader from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import ConfirmDialog from '../../components/ui/confirm-dialog';
import { FormSkeleton } from '../../components/ui/skeleton';
import {
  extractSlotsFromResponses,
  guessFieldMapping,
  loadInvitationWithResponses,
  resolveUniqueEventId,
  saveEvent,
} from '../../lib/events';
import { getLeafFields } from '../../lib/invitation';
import { toast } from '../../lib/toast';

function NewEvent() {
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eventName, setEventName] = useState('');
  const [selectedInvitationId, setSelectedInvitationId] = useState('');
  const [fieldMapping, setFieldMapping] = useState({
    name: '', surname: '', phone: '', email: '', availability: '',
  });
  const [preview, setPreview] = useState({ fields: [], slots: [], participantCount: 0 });
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    axios.get(`${dbUrl}invitations.json`)
      .then((res) => {
        const list = res.data
          ? Object.entries(res.data).map(([id, inv]) => ({ id, name: inv.name || id }))
          : [];
        setInvitations(list.sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedInvitationId) {
      setPreview({ fields: [], slots: [], participantCount: 0 });
      return;
    }

    loadInvitationWithResponses(selectedInvitationId)
      .then((data) => {
        if (!data) return;
        const mapping = guessFieldMapping(data.fields);
        setFieldMapping(mapping);
        const slots = extractSlotsFromResponses(data.responses, mapping, data.fields);
        setPreview({
          fields: getLeafFields(data.fields).map((f) => f.name),
          slots,
          participantCount: data.responses.length,
        });
      })
      .catch(console.error);
  }, [selectedInvitationId]);

  const handleCreateClick = () => {
    setError('');
    if (!eventName.trim()) {
      setError('Inserisci un nome per l\'evento.');
      return;
    }
    if (!selectedInvitationId) {
      setError('Seleziona un invito sorgente.');
      return;
    }
    if (!fieldMapping.name || !fieldMapping.availability) {
      setError('Mappa almeno i campi Nome e Disponibilità/turno.');
      return;
    }
    setConfirmOpen(true);
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const data = await loadInvitationWithResponses(selectedInvitationId);
      if (!data) throw new Error('Invito non trovato');

      const slots = extractSlotsFromResponses(data.responses, fieldMapping, data.fields);
      const eventId = await resolveUniqueEventId(eventName);
      const now = Date.now();

      const event = {
        id: eventId,
        name: eventName.trim(),
        sourceInvitationId: selectedInvitationId,
        fieldMapping,
        stores: [],
        assignments: [],
        boardNotes: [],
        slots,
        knownResponseIds: data.responses.map((r) => r.id),
        createdAt: now,
        updatedAt: now,
      };

      await saveEvent(event);
      toast.success('Evento creato', `"${eventName.trim()}" è pronto per il planner.`);
      navigate(`/eventi/${eventId}`);
    } catch (e) {
      console.error(e);
      setError('Errore durante la creazione dell\'evento.');
      toast.error('Creazione non riuscita', 'Verifica i dati e riprova.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6 md:py-8">
        <PageHeader
          title="Nuovo evento"
          description="Caricamento in corso…"
          backTo="/eventi"
          backLabel="Torna agli eventi"
        />
        <Card>
          <CardContent className="p-6">
            <FormSkeleton fields={5} />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 md:py-8">
      <PageHeader
        title="Nuovo evento"
        description="Collega un invito esistente per importare i partecipanti e organizzare i turni nei negozi."
        backTo="/eventi"
        backLabel="Torna agli eventi"
      />

      <Card>
        <CardContent className="space-y-5 p-4 md:p-6">
          <div>
            <label className="ba-label">Nome evento</label>
            <Input
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder="es. Raccolta alimentare marzo 2026"
            />
            <p className="mt-1 ba-muted">Potrai rinominarlo in seguito dal planner.</p>
          </div>

          <div>
            <label className="ba-label">Invito sorgente</label>
            <select
              className="ba-input"
              value={selectedInvitationId}
              onChange={(e) => setSelectedInvitationId(e.target.value)}
            >
              <option value="">— Seleziona invito —</option>
              {invitations.map((inv) => (
                <option key={inv.id} value={inv.id}>{inv.name}</option>
              ))}
            </select>
            {preview.participantCount > 0 && (
              <p className="mt-1 text-sm text-brand-green">
                {preview.participantCount} risposte trovate · Turni: {preview.slots.join(', ') || '—'}
              </p>
            )}
          </div>

          {selectedInvitationId && (
            <div className="space-y-3 rounded-xl border border-[var(--ba-surface-border)] p-4">
              <p className="text-sm font-semibold">Mappa i campi risposta</p>
              {[
                { key: 'name', label: 'Nome', required: true },
                { key: 'surname', label: 'Cognome', required: false },
                { key: 'availability', label: 'Disponibilità / turno', required: true },
                { key: 'phone', label: 'Telefono', required: false },
                { key: 'email', label: 'Email', required: false },
              ].map(({ key, label, required }) => (
                <div key={key}>
                  <label className="ba-label">
                    {label}{required ? ' *' : ' (opzionale)'}
                  </label>
                  <select
                    className="ba-input"
                    value={fieldMapping[key]}
                    onChange={(e) => setFieldMapping((m) => ({ ...m, [key]: e.target.value }))}
                  >
                    <option value="">— Nessuno —</option>
                    {preview.fields.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          {error && <p className="text-sm text-brand-danger">{error}</p>}

          <div className="flex flex-wrap gap-3">
            <Button type="button" onClick={handleCreateClick} disabled={saving}>
              {saving ? 'Creazione…' : 'Crea e apri planner'}
            </Button>
            <Button asChild variant="outline">
              <Link to="/eventi">Annulla</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Creare l'evento?"
        description={`Verrà creato l'evento "${eventName.trim()}" con ${preview.participantCount} partecipanti dall'invito selezionato.`}
        confirmLabel="Crea evento"
        onConfirm={handleCreate}
      />
    </div>
  );
}

export default NewEvent;
